import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, EmptyState, cx, fieldClass, labelClass } from '@timely/ui';
import {
    STOCK_FIELDS, parseSheetLink, stockMappingIsUsable, suggestStockMapping,
    type StockColumnMap,
} from '@timely/domain';
import { apiPost } from '../lib/api';
import { supabase } from '../lib/supabaseClient';
import { FormNotice } from '../screens/AuthLayout';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { selectGoogleSheet, useGoogleConnection } from '../datasource/google';
import { type ChosenGoogleSheet, GoogleAccountCard, GoogleSheetChooser } from '../datasource/GoogleConnect';

interface CheckResponse {
    source: { spreadsheetId: string; gid: string | null; connectionType: 'export_csv' | 'published_csv' };
    headers: string[];
    sample: string[][];
    rowCount: number;
}

/** What the mapping step needs, whichever way the sheet was reached. */
interface StockDraft {
    headers: string[];
    rowCount: number;
    google?: { spreadsheetId: string; sheetId: number; title: string };
    link?: CheckResponse['source'];
}

/**
 * Connecting a stock sheet.
 *
 * The same Google connection as the receivables sheet (08 §14): one grant per
 * company, a spreadsheet chosen in Google's own file chooser, an exact
 * numbered tab, and the column map checked by the server against that tab's
 * real headers before it is saved. The "anyone with the link" path is offered
 * only in development builds, as on the data source screen.
 */
export function StockConnect({
    workspaceId, canManage, statusDetail, onConnected,
    allowPublicLinks = import.meta.env.DEV,
}: {
    workspaceId: string;
    canManage: boolean;
    statusDetail: string | null;
    onConnected: () => void;
    allowPublicLinks?: boolean;
}) {
    const { workspace } = useActiveWorkspace();
    const google = useGoogleConnection(workspaceId);
    const [params, setParams] = useSearchParams();
    const [link, setLink] = useState('');
    const [checking, setChecking] = useState(false);
    const [draft, setDraft] = useState<StockDraft | null>(null);
    const [columns, setColumns] = useState<StockColumnMap>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Back from Google: the chooser is already showing; say what to do next.
    const [returned, setReturned] = useState(false);
    useEffect(() => {
        if (params.get('google') !== 'connected') return;
        setReturned(true);
        params.delete('google');
        setParams(params, { replace: true });
    }, [params, setParams]);

    if (!canManage) {
        return (
            <Card>
                <EmptyState
                    title="No stock sheet connected"
                    hint="Once somebody who manages receivables data connects one, it appears here."
                />
            </Card>
        );
    }

    const begin = (next: StockDraft) => {
        setDraft(next);
        setColumns(suggestStockMapping(next.headers));
    };

    const runCheck = async () => {
        setError(null);
        setChecking(true);
        try {
            const result = await apiPost<CheckResponse>('/api/datasource/check', { workspaceId, link });
            begin({ headers: result.headers, rowCount: result.rowCount, link: result.source });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'That link could not be read.');
        } finally {
            setChecking(false);
        }
    };

    const fromGoogle = (chosen: ChosenGoogleSheet) => begin({
        headers: chosen.tab.headers,
        rowCount: chosen.tab.rowCount,
        google: { spreadsheetId: chosen.spreadsheetId, sheetId: chosen.tab.sheetId, title: chosen.tab.title },
    });

    const save = async () => {
        if (!draft) return;
        setError(null);
        setSaving(true);
        try {
            if (draft.google) {
                await selectGoogleSheet(workspaceId, 'stock', {
                    spreadsheetId: draft.google.spreadsheetId,
                    sheetId: draft.google.sheetId,
                    columns: columns as Record<string, string>,
                });
            } else if (draft.link) {
                const { error: saveError } = await supabase()
                    .from('workspace_integrations')
                    .upsert({
                        workspace_id: workspaceId,
                        kind: 'stock',
                        provider: 'google_sheets',
                        spreadsheet_id: draft.link.spreadsheetId,
                        sheet_gid: draft.link.gid,
                        connection_type: draft.link.connectionType,
                        // A stock row has no identity to reconcile against an
                        // existing customer, so this column — meaningful only for
                        // receivables — is left at its default rather than
                        // repurposed to mean something it does not here.
                        config: { columns },
                        status: 'ok',
                        status_detail: null,
                        last_checked_at: new Date().toISOString(),
                    }, { onConflict: 'workspace_id,kind' });
                if (saveError) throw new Error(
                    /row-level security|permission denied/i.test(saveError.message)
                        ? 'You do not have permission to change this company’s stock source.'
                        : 'We could not save that connection. Try again in a moment.',
                );
            }
            setDraft(null);
            setLink('');
            onConnected();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'We could not save that connection.');
        } finally {
            setSaving(false);
        }
    };

    const usable = draft ? stockMappingIsUsable(draft.headers, columns) : false;

    return (
        <>
            {!google.loading && (
                <GoogleAccountCard
                    workspaceId={workspaceId}
                    slug={workspace.slug}
                    kind="stock"
                    status={google.status}
                    canManage={canManage}
                    onChanged={() => void google.reload()}
                />
            )}

            <Card className="mt-5 p-5">
                <h3 className="text-subhead font-semibold text-label tracking-heading">Connect a stock sheet</h3>
                <p className="text-secondary text-label-2 mt-1">
                    The Stock screen reads this sheet directly, every time it opens. Nothing from it is
                    imported or stored — only the connection itself is saved.
                </p>
                {returned && !draft && (
                    <div className="mt-3"><FormNotice tone="ok">Google is connected. Now choose your stock sheet.</FormNotice></div>
                )}
                {statusDetail && <div className="mt-3"><FormNotice tone="error">{statusDetail}</FormNotice></div>}
                {error && <div className="mt-3"><FormNotice tone="error">{error}</FormNotice></div>}

                {draft ? (
                    <div className="mt-4">
                        <p className="text-secondary text-label-2">
                            Found {draft.rowCount} row{draft.rowCount === 1 ? '' : 's'}
                            {draft.google ? ` in “${draft.google.title}”` : ''}. Confirm what each column means:
                        </p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                            {STOCK_FIELDS.map(def => (
                                <div key={def.field}>
                                    <label htmlFor={`stock-column-${def.field}`} className={labelClass}>
                                        {def.label}{def.requirement === 'required' && ' *'}
                                    </label>
                                    <select
                                        id={`stock-column-${def.field}`}
                                        value={columns[def.field] ?? ''}
                                        onChange={e => setColumns(prev => ({
                                            ...prev,
                                            [def.field]: e.target.value || undefined,
                                        } as StockColumnMap))}
                                        className={fieldClass({ className: 'w-full' })}
                                    >
                                        <option value="">Not in this sheet</option>
                                        {draft.headers.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 flex gap-2">
                            <Button variant="primary" disabled={!usable || saving} onClick={() => { void save(); }} busy={saving}>
                                {saving ? 'Saving…' : 'Save connection'}
                            </Button>
                            <Button variant="secondary" onClick={() => setDraft(null)}>Back</Button>
                        </div>
                    </div>
                ) : google.status.connected ? (
                    <GoogleSheetChooser workspaceId={workspaceId} status={google.status} onChosen={fromGoogle} />
                ) : null}

                {allowPublicLinks && !draft && (
                    <div className="mt-5 pt-4 border-t border-separator flex flex-wrap gap-2">
                        {/* A placeholder disappears the moment somebody types, and a
                            screen reader may not read it at all: the field needs a name. */}
                        <label htmlFor="stock-sheet-link" className={cx(labelClass, 'w-full')}>
                            Link to the stock sheet — for local testing only
                        </label>
                        <input
                            id="stock-sheet-link"
                            type="text"
                            autoComplete="off"
                            value={link}
                            onChange={e => setLink(e.target.value)}
                            placeholder="Paste the link to your Google Sheet"
                            className={fieldClass({ className: 'flex-1 min-w-60' })}
                        />
                        <Button
                            variant="primary"
                            disabled={checking || !isPlausibleLink(link)}
                            onClick={() => { void runCheck(); }}
                         busy={checking}>
                            {checking ? 'Reading…' : 'Read it'}
                        </Button>
                    </div>
                )}
            </Card>
        </>
    );
}

const isPlausibleLink = (raw: string): boolean => {
    if (!raw.trim()) return false;
    const parsed = parseSheetLink(raw);
    return parsed.ok;
};
